import isEqual from "lodash/isEqual";
import { Component } from "react";
import { FilterItem } from "@app/resource-filters/filter-items";
import { FilteredResource, Resource, ResourceStatus } from "@app/resources";
import { ResultLookup } from "@app/home//homeInterfaces";
import RegionTable from "@app/home/dashboard-summary/RegionTable";
import SpecialtyTable from "@app/home/dashboard-summary/SpecialtyTable";
import CrossProjectGaps from "@app/home/cross-project-gaps/CrossProjectGaps";
import MembershipRiskTable from "@app/home/dashboard-summary/membershipRiskTable";
import { FlyoutLink } from "@app/shared/Flyouts";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import SummaryTile from "@app/shared/SummaryTile";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import ProjectDashboardModel from "@app/projects/projectDashboardModel";
import ProjectService from "@app/projects/projectService";
import Roles from "@app/user/roles";
import RoleRestricted from "@app/user/RoleRestricted";

interface DashboardSummaryProps {
  filteredResources: FilteredResource[];
  handleAddFilter: (newFilterItem: FilterItem) => void;
  hasSpecialtyFilter: boolean;
  loading: boolean;
  projectDashboardModel: ProjectDashboardModel;
  projectResources: Resource[];
  showingArchived: boolean;
  showingHidden: boolean;
  summaries: ResultLookup;
}

interface DashboardSummaryState {
  projectDashboardModel: ProjectDashboardModel;
  triggerCloseRegionTableSidePanel: () => void;
  triggerCloseSpecialtyTableSidePanel: () => void;
}

export default class DashboardSummary extends Component<DashboardSummaryProps, DashboardSummaryState> {
  private readonly projectService = new ProjectService();

  constructor(props: DashboardSummaryProps) {
    super(props);

    this.state = {
      projectDashboardModel: this.props.projectDashboardModel,
      triggerCloseRegionTableSidePanel: null,
      triggerCloseSpecialtyTableSidePanel: null,
    };
  }

  public async componentDidUpdate(prevProps: DashboardSummaryProps) {
    if (prevProps.loading && !this.props.loading) {
      this.setState({
        projectDashboardModel: this.props.projectDashboardModel,
      });
    } else if (this.props.projectDashboardModel && !this.props.loading && !isEqual(prevProps.summaries, this.props.summaries) && isEqual(prevProps.projectDashboardModel, this.props.projectDashboardModel)) {
      //reload if the summaries changed but the dashboard model hasn't (which happens when a project runs)
      //if not for the tile view then we could just reload the dashboard model in the home component when a project run notification is received
      //but doing it there with the tiles causes every tile to re-render, which is ugly.
      //This is cleaner but also leads to more code maintenance, since we have to do this, as well as handle the job notifications in two files (ProjectTile and ProjectTable)
      const projectDashboardModel = this.props.filteredResources && this.props.filteredResources.length
        ? await this.projectService.projectTileResults(this.props.filteredResources, this.props.showingArchived ? ResourceStatus.Archived : ResourceStatus.Active, true, this.props.showingHidden)
        : {
          countyCount: 0,
          gapCount: 0,
          percentGaps: 0,
          projectCount: 0,
          projectTileAggregates: [],
          totalCount: 0,
        };
      this.setState({
        projectDashboardModel,
      });
    }

  }

  public render() {
    return (
      <section className="dashboard-summary">
        <SummaryTile
          title="Projects"
          content={this.renderNumberOrLoading(this.props.loading ? 0 : this.state.projectDashboardModel.projectCount)}
          infoIndicatorText="Project count and summaries data for County level projects."
        />
        <SummaryTile
          infoIndicatorText="The total, non-distinct number of county instances across all projects represented in the current dashboard."
          title="Counties"
          content={this.renderNumberOrLoading(this.props.loading ? 0 : this.state.projectDashboardModel.countyCount)}
          topRightContent={this.props.loading ? null : this.renderRegionTableSidePanelLink()}
        />
        <SummaryTile
          topRightContent={this.renderCrossProjectGapsLink()}
          title="Gaps"
          content={this.renderNumberOrLoading(this.props.loading ? 0 : this.state.projectDashboardModel.gapCount)}
        />
        <SummaryTile
          infoIndicatorText="The total, non-distinct number of county/specialty group combination instances across all projects represented in the current dashboard."
          title="County/Specialty Groups"
          content={this.renderNumberOrLoading(this.props.loading ? 0 : this.state.projectDashboardModel.totalCount)}
        />
        <SummaryTile
          title="Gap Percentage"
          content={this.renderNumberOrLoading(this.props.loading ? 0 : parseFloat((this.state.projectDashboardModel.percentGaps * 100).toFixed(1)))}
          isPercent={!this.props.loading}
          topRightContent={this.props.loading ? null : this.renderSpecialtyTableSidePanelLink()}
        />
        {!this.props.hasSpecialtyFilter && (
          <RoleRestricted allowedRoles={[Roles.MEMBERSHIP_RISK_USER]}>
            <SummaryTile
              title="Membership Risk"
              content={this.renderNumberOrLoading(this.props.loading ? 0 : this.state.projectDashboardModel.percentMembershipRisk)}
              isPercent={!this.props.loading}
              topRightContent={this.props.loading ? null : this.renderMembershipRiskSidePanelLink()}
            />
          </RoleRestricted>
        )}
      </section>
    );
  }

  private renderCrossProjectGapsLink = () => {
    return (
      <FlyoutLink
        content={{
          body: (<CrossProjectGaps
            filteredProjectResources={this.props.filteredResources}
            projectResources={this.props.projectResources}
            resourceStatus={this.props.showingArchived ? ResourceStatus.Archived : ResourceStatus.Active}
          />),
          title: "Gaps by County and Specialty",
        }}
        testId="dashboard-summary__cross-project-gaps-flyout"
        classNames="p-0 text-dark"
      >
        <div className="ms-1 mb-1"><QesIcon iconName={QesIcons.ShareV4} description="Open gaps by county and specialty" /></div>
      </FlyoutLink>
    );
  }

  private renderRegionTableSidePanelLink = () => {
    return (
      <TooltipWrapper text="Gaps by County">
        <FlyoutLink
          content={{
            body: (
              <RegionTable
                filteredResources={this.props.filteredResources}
                handleAddFilter={this.handleAddRegionFilter}
                showingArchived={this.props.showingArchived}
              />
            ),
            title: "Gaps by County",
          }}
          testId="dashboard-summary__region-table-flyout"
          classNames="p-0 text-dark"
        >
          <div className="ms-1 mb-1"><QesIcon iconName={QesIcons.ShareV4} description="Open gaps by county" /></div>
        </FlyoutLink>
      </TooltipWrapper>
    );
  }

  private renderSpecialtyTableSidePanelLink = () => {
    return (
      <TooltipWrapper text="Gaps by Specialty Group">
        <FlyoutLink
          content={{
            body: (
              <SpecialtyTable
                filteredResources={this.props.filteredResources}
                handleAddFilter={this.handleAddSpecialtyFilter}
                showingArchived={this.props.showingArchived}
              />
            ),
            title: "Gaps by Specialty Group",
          }}
          testId="dashboard-summary__specialty-table-flyout"
          classNames="p-0 text-dark"
        >
          <div className="ms-1 mb-1"><QesIcon iconName={QesIcons.ShareV4} description="Open gaps by specialty group" /></div>
        </FlyoutLink>
      </TooltipWrapper>
    );
  }

  private renderMembershipRiskSidePanelLink = () => (
    <TooltipWrapper text="Membership Risk Analysis">
      <FlyoutLink
        content={{
          body: (
            <MembershipRiskTable
              filteredResources={this.props.filteredResources}
              projectResources={this.props.projectResources}
              showingArchived={this.props.showingArchived}
            />
          ),
          title: "Counties with Membership at Risk",
        }}
        testId="dashboard-summary__membership-risk-flyout"
        classNames="p-0 text-dark"
      >
        <div className="ms-1 mb-1"><QesIcon iconName={QesIcons.ShareV4} description="Open counties with membership at risk" /></div>
      </FlyoutLink>
    </TooltipWrapper>
  )

  private handleAddRegionFilter = (newFilterItem: FilterItem) => {
    this.props.handleAddFilter(newFilterItem);
  }

  private handleAddSpecialtyFilter = (newFilterItem: FilterItem) => {
    this.props.handleAddFilter(newFilterItem);
  }

  private renderNumberOrLoading = (value: number, localize = true) => {
    if (this.props.loading) {
      return <LoadingSpinner />;
    }
    return <span>{(isNaN(value) ? "---" : localize ? value.toLocaleString("en-US") : value.toString())}</span>;
  }
}
